/// <reference path="../../common/common.module.ts" />

/**
 * Web Component Bridge Service
 *
 * Provides host runtime context (auth, session, API base URL, security keys,
 * app settings) and a pub/sub lifecycle event system to any embedded web
 * component. This service is domain-agnostic.
 *
 * Registered on `meevo.components.common` so it is available to all plugins.
 *
 * --- Bridge contract ---
 * Web components receive this service as a property (default: `hostBridge`).
 * They use:
 *   bridge.initialize()                → initial context snapshot
 *   bridge.getCurrentContext()          → on-demand context refresh
 *   bridge.subscribe(listener)         → lifecycle events (context-changed, session-state-changed)
 *   bridge.refreshLockoutLastEventTime → keep lockout timer alive
 */
interface ServerEvent<T = any> {
    selector: string;
    payload: T;
    receivedAt: number;
    source: 'live' | 'mock';
}

angular.module('meevo.components.common').factory('webComponentBridge', [
    '$injector',
    '$rootScope',
    'meevo.AppSettings',
    'meevo.components.common.IdentityService',
    function (
        $injector: angular.auto.IInjectorService,
        $rootScope: angular.IRootScopeService,
        appSettings: Meevo.Components.Common.IAppSettings,
        identityService: Meevo.Components.Common.IIdentityService,
    ) {
        const LOG_PREFIX = '[WebComponentBridge][HostInterop]';
        const listeners: Array<(event: any) => void> = [];
        const serverEventListeners = new Set<(event: ServerEvent) => void>();
        const clientCreatedListeners = new Set<(result: any) => void>();
        let lastIdentitySignature = '';

        function log(message: string, details?: any): void {
            if (details === undefined) {
                console.log(LOG_PREFIX + ' ' + message);
                return;
            }
            console.log(LOG_PREFIX + ' ' + message, details);
        }

        function warn(message: string, details?: any): void {
            if (details === undefined) {
                console.warn(LOG_PREFIX + ' ' + message);
                return;
            }
            console.warn(LOG_PREFIX + ' ' + message, details);
        }

        function contextSnapshot(context: any): any {
            return {
                hasContext: !!context,
                apiBaseUrl: context ? context.apiBaseUrl : null,
                hasSession: !!(context && context.session),
                session: context && context.session
                    ? {
                        tenantId: context.session.tenantId || null,
                        locationId: context.session.locationId || null,
                        locationName: context.session.locationName || null,
                        userName: context.session.userName || context.session.userAuthName || null,
                        hasBearerToken: !!context.session.bearerToken,
                    }
                    : null,
                securityItemCount: context && context.securityItems ? context.securityItems.length : 0,
            };
        }

        function getIdentitySignature(identity: Meevo.Components.Common.IIdentity | null | undefined): string {
            if (!identity) {
                return '';
            }
            return [
                identity.userId,
                identity.locationId,
                identity.effectiveCallCenterId,
                identity.asImpersonateLocationId,
            ].join('|');
        }

        async function resolveSecurityItems(): Promise<any[]> {
            try {
                const resolved = await Promise.resolve(appSettings.SECURITY_ACCESS_KEYS);
                return Array.isArray(resolved) ? resolved : [];
            } catch (_error) {
                warn('failed to resolve security items; returning empty array');
                return [];
            }
        }

        function getInteropService(): any | null {
            if (!$injector.has('meevo.plugins.interop.InteropService')) {
                return null;
            }
            try {
                return $injector.get('meevo.plugins.interop.InteropService');
            } catch (_error) {
                warn('InteropService resolve threw; returning null');
                return null;
            }
        }

        async function buildContext(): Promise<any> {
            const interopService = getInteropService();

            if (interopService && typeof interopService.getHostRuntimeContext === 'function') {
                const interopContext = await Promise.resolve(interopService.getHostRuntimeContext());
                log('buildContext from InteropService.getHostRuntimeContext', contextSnapshot(interopContext));
                return interopContext;
            }

            // Fallback: assemble context directly from shell services
            const identity = identityService.current();
            const session = identity
                ? {
                    ...identity,
                    bearerToken: (window as any).bearerToken !== undefined && (window as any).bearerToken !== null
                        ? (window as any).bearerToken
                        : null,
                }
                : null;
            const securityItems = await resolveSecurityItems();

            const context = {
                apiBaseUrl: `/${appSettings.API_ROOT}`,
                session,
                appSettings: {
                    API_ROOT: appSettings.API_ROOT,
                    APP_COUNTRYID: appSettings.APP_COUNTRYID,
                    APP_LANGUAGEID: appSettings.APP_LANGUAGEID,
                    APP_TIME_FORMAT: appSettings.APP_TIME_FORMAT,
                    APP_ISOCOUNTRYCODE: appSettings.APP_ISOCOUNTRYCODE,
                    APP_FIELDLABELS: appSettings.APP_FIELDLABELS,
                    APP_BUSINFOSETTINGS: appSettings.APP_BUSINFOSETTINGS,
                    SECURITY_ACCESS_KEYS: securityItems,
                },
                securityItems,
            };

            log('buildContext from fallback identity/appSettings', contextSnapshot(context));
            return context;
        }

        async function emit(event: any): Promise<void> {
            const snapshot = listeners.slice();
            for (const listener of snapshot) {
                try {
                    listener(event);
                } catch (error) {
                    console.warn('[WebComponentBridge] Listener failed.', error);
                }
            }
        }

        async function emitContextChanged(reason: string): Promise<void> {
            await emit({
                type: 'context-changed',
                reason,
                context: await buildContext(),
            });
        }

        // --- Lifecycle event listeners ---
        $rootScope.$on(Meevo.Components.Common.Messages.AUTHENTICATION_SUCCESS, async () => {
            const identity = identityService.current();
            const nextSignature = getIdentitySignature(identity);
            const reason = lastIdentitySignature && lastIdentitySignature !== nextSignature
                ? 'account-switch'
                : 'authentication-success';
            lastIdentitySignature = nextSignature;
            await emitContextChanged(reason);
        });

        $rootScope.$on(Meevo.Components.Common.Messages.REFRESH_SECURITY_KEYS, async () => {
            await emitContextChanged('session-refresh');
        });

        $rootScope.$on(Meevo.Components.Common.Messages.FORCE_SESSION_RELOAD, async () => {
            await emitContextChanged('force-session-reload');
        });

        $rootScope.$on(
            Meevo.Components.Common.Messages.SESSION_TIMEOUT_HANDLER,
            async (_event: angular.IAngularEvent, sessionState: Meevo.Model.SessionState) => {
                await emit({
                    type: 'session-state-changed',
                    reason: sessionState === Meevo.Model.SessionState.Lockout ? 'lockout' : 'logout',
                    context: await buildContext(),
                });
            }
        );

        return {
            initialize: async function () {
                const context = await buildContext();
                lastIdentitySignature = getIdentitySignature(context.session);
                return context;
            },
            getCurrentContext: function () {
                return buildContext();
            },
            refreshLockoutLastEventTime: function () {
                const interopService = getInteropService();
                if (interopService && typeof interopService.refreshLockoutLastEventTime === 'function') {
                    interopService.refreshLockoutLastEventTime();
                    return;
                }
                $rootScope.$broadcast(Meevo.Components.Common.Messages.REFRESH_LOCKOUT_LAST_EVENT_TIME);
            },
            subscribe: function (listener: (event: any) => void) {
                listeners.push(listener);
                return function () {
                    const index = listeners.indexOf(listener);
                    if (index >= 0) {
                        listeners.splice(index, 1);
                    }
                };
            },
            subscribeServerEvents: function (listener: (event: ServerEvent) => void): () => void {
                serverEventListeners.add(listener);
                return function () {
                    serverEventListeners.delete(listener);
                };
            },
            dispatchServerEvent: function <T>(selector: string, payload: T, source: 'live' | 'mock' = 'live'): void {
                const event: ServerEvent<T> = {
                    selector,
                    payload,
                    receivedAt: performance.now(),
                    source,
                };
                const snapshot = Array.from(serverEventListeners);
                for (const fn of snapshot) {
                    try {
                        fn(event);
                    } catch (e) {
                        console.error(LOG_PREFIX + ' server-event listener threw', e);
                    }
                }
            },
            /**
             * Diagnostic — returns the current listener count. Used by the
             * mount directive to detect listener leaks across mount/unmount
             * cycles. Not part of the component-facing contract.
             */
            getListenerCount: function (): number {
                return listeners.length;
            },

            /**
             * Ask the host shell to open the Client maintenance form in Adding
             * mode and navigate back to the appointment book on save/cancel.
             * The result is delivered to all `subscribeClientCreated` listeners.
             */
            openClientMaintenance: function (request: { correlationId: string }): void {
                const correlationId = request && request.correlationId ? request.correlationId : null;
                $rootScope.$apply(function () {
                    $rootScope.$broadcast('gotoState', {
                        stateName: 'client',
                        stateParams: {
                            callback: function (callbackArgs: { model: any; callbackData: any; callbackContext: any }): void {
                                const model = callbackArgs && callbackArgs.model;
                                const result = {
                                    correlationId: correlationId,
                                    client: model
                                        ? {
                                            id: model.id,
                                            firstName: model.firstName || null,
                                            lastName: model.lastName || null,
                                            displayName: model.displayName
                                                || ((model.firstName || '') + ' ' + (model.lastName || '')).trim()
                                                || null,
                                        }
                                        : null,
                                };
                                const snapshot = Array.from(clientCreatedListeners);
                                for (let i = 0; i < snapshot.length; i++) {
                                    try { snapshot[i](result); } catch (e) {
                                        console.error(LOG_PREFIX + ' clientCreated listener threw', e);
                                    }
                                }
                            },
                            callbackController: true,
                            callbackState: 'appointmentBook',
                            targetState: Meevo.Model.MaintenanceFormVisualState.Detail,
                            actionState: Meevo.Model.MaintenanceFormState.Adding,
                            id: undefined,
                            initialEditSection: 'SECTION_CLIENT_MAIN',
                            typeaheadMode: Meevo.Model.TypeaheadMode.Add,
                        },
                    });
                });
            },

            /**
             * Register a listener that fires whenever Client maintenance
             * completes (save or cancel). Returns an unsubscribe function.
             */
            subscribeClientCreated: function (listener: (result: any) => void): () => void {
                clientCreatedListeners.add(listener);
                return function (): void {
                    clientCreatedListeners.delete(listener);
                };
            },

            /** Open an existing client's profile read-only in the Client tab. */
            openClientProfile: function (clientId: string): void {
                if (!clientId) {
                    return;
                }
                $rootScope.$apply(function () {
                    $rootScope.$broadcast('gotoState', {
                        stateName: appSettings.ROUTE_NAMES.client,
                        stateParams: {
                            callback: null,
                            callbackState: null,
                            targetState: Meevo.Model.MaintenanceFormVisualState.Detail,
                            actionState: Meevo.Model.MaintenanceFormState.Readonly,
                            id: clientId,
                        },
                    });
                });
            },

            /**
             * Open a specific appointment in the appointment book editor. The optional
             * fields exist for parity with the other EditAppointment callers; both the
             * enhanced and legacy books tolerate them being undefined.
             */
            openAppointment: function (request: {
                id: string;
                appointmentId: string;
                appointmentDate: string | Date;
                finalState?: any;
                appointmentServiceScheduleId?: string;
                appointmentScheduleEntityVersion?: any;
                employeeId?: string;
                returnStateInfo?: {
                    returnStateSection: string;
                    id?: any;
                    initialEditSection?: any;
                    actionState?: any;
                    targetState?: any;
                };
            }): void {
                if (!request || !request.id || !request.appointmentId || !request.appointmentDate) {
                    return;
                }
                $rootScope.$apply(function () {
                    $rootScope.$broadcast('gotoState', {
                        stateName: appSettings.ROUTE_NAMES.appointmentBook,
                        stateParams: {
                            actionState: Meevo.Model.AppointmentBookState.EditAppointment,
                            actionStatePayload: {
                                finalState: request.finalState,
                                id: request.id,
                                appointmentId: request.appointmentId,
                                appointmentServiceScheduleId: request.appointmentServiceScheduleId,
                                appointmentScheduleEntityVersion: request.appointmentScheduleEntityVersion,
                                appointmentDate: request.appointmentDate,
                                employeeId: request.employeeId,
                                returnStateInfo: request.returnStateInfo,
                            },
                        },
                    });
                });
            },

            /**
             * Ask the host shell to open the Online Booking Settings screen.
             * Used by the Offer Builder "Enable the new Online Booking experience"
             * link when the location's OB module is on but the new OB setting is off.
             */
            openOnlineBookingSettings: function (): void {
                $rootScope.$apply(function () {
                    $rootScope.$broadcast('gotoState', {
                        stateName: appSettings.ROUTE_NAMES.onlineBookingSettings,
                    });
                });
            },

            /**
             * Ask the host shell to open MeevoXchange, deep-linked to the given
             * `Meevo.LinkType`. Routes through the meevoStore context; MeevoStoreController
             * maps the link type to the right sub-page (e.g. BusinessBoosters → Add-Ons).
             */
            openMeevoXchange: function (linkType: Meevo.LinkType): void {
                // Business Boosters lands on a dedicated sub-page (.addons) that differs from
                // the meevoStore context's deepStateRedirect fallback. DSR remembers the last
                // sub-state across tab close/reopen (removeContext never clears it), so a
                // re-open would restore the remembered page instead of re-routing by linkType.
                // Reset DSR for this context so HomeController re-runs the linkType routing on
                // every open. Scoped to Business Boosters — other link types target the DSR
                // fallback (account), so their remembered sub-state is already correct.
                if (linkType === Meevo.LinkType.BusinessBoosters && $injector.has('$deepStateRedirect')) {
                    try {
                        var deepStateRedirect = $injector.get('$deepStateRedirect') as any;
                        deepStateRedirect.reset(appSettings.ROUTE_NAMES.meevoStore);
                    } catch (e) {
                        console.warn(LOG_PREFIX + ' $deepStateRedirect.reset failed', e);
                    }
                }
                $rootScope.$apply(function () {
                    $rootScope.$broadcast('gotoState', {
                        stateName: appSettings.ROUTE_NAMES.meevoStore,
                        stateParams: { linkType: linkType },
                    });
                });
            },
        };
    }
]);
